import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { UseFormik } from '@/utils/Formik/types';
import { compareList } from '@/utils/Sorting';

import { getHouseHoldLinen } from '@/services/GuestDamage/request';

import { FileProps } from '@/context/FileContext/types';
import { useUser } from '@/context/UserContext';

import { useGuestDamage } from '@/hooks/GuestDamage/useGuestDamage';

import FormButton from '@/components/FormButton/FormButton';
import { NumberInput } from '@/components/GuestDamage/components/NumberInput';
import { UploadInput } from '@/components/GuestDamage/components/UploadInput';
import SimpleSelect, { SelectOption } from '@/components/SimpleSelect/SimpleSelect';
import TextField from '@/components/TextField/TextField';

import { ButtonContainer, FullDiv, GridContainer, TrousseauFormContainer } from './styles';

const needToDoneOptions: SelectOption[] = [
  {
    value: 'Repor item',
    valueLabel: 'Repor item',
  },
  {
    value: 'Conserto',
    valueLabel: 'Conserto',
  },
  {
    value: 'Limpeza',
    valueLabel: 'Limpeza',
  },
  {
    value: 'Contratar Serviço Terceirizado',
    valueLabel: 'Contratar Serviço Terceirizado',
  },
  {
    value: 'Cobrar Pendência',
    valueLabel: 'Cobrar Pendência',
  },
].sort((a, b) => compareList(a.valueLabel, b.valueLabel));

interface TrousseauFormProps {
  formik: UseFormik<any>;
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  isEditingForm: boolean;
}

const TrousseauForm = ({ quantity, formik, setQuantity, isEditingForm }: TrousseauFormProps) => {
  const {
    evidences,
    setEvidences,
    quotationFile,
    setQuotationFile,
    selectedGuestDamage,
    setHouseHoldLinenItens,
  } = useGuestDamage();
  const { userInformation } = useUser();
  const roles = userInformation?.roles;
  const [trousseauDamageOptions, setTrousseauDamageOptions] = useState<SelectOption[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  async function getGuestDamageHouseHoldLinen() {
    const houseHoldLinenGetItens = await getHouseHoldLinen();
    if (houseHoldLinenGetItens && houseHoldLinenGetItens.length > 0) {
      const options: SelectOption[] = houseHoldLinenGetItens.map((option) => ({
        value: option?.name || '',
        valueLabel: option?.name || '',
      }));
      const allOptions: SelectOption[] = [...options, { valueLabel: 'Outro', value: 'Outro' }];
      setTrousseauDamageOptions(
        allOptions.sort((a, b) => compareList(a?.valueLabel || '', b?.valueLabel || '')),
      );
      setHouseHoldLinenItens(houseHoldLinenGetItens);
    }
  }

  useEffect(() => {
    getGuestDamageHouseHoldLinen();
  }, []);

  useEffect(() => {
    if (selectedGuestDamage) {
      if (selectedGuestDamage.evidences && selectedGuestDamage.evidences.length > 0) {
        const formattedEvidences = selectedGuestDamage.evidences.map((selectedEvidence) => {
          if (selectedEvidence?.evidence) {
            return selectedEvidence.evidence;
          }
          return null;
        });
        const filteredGuestDamage = formattedEvidences.filter(
          (selectedEvidence) => selectedEvidence !== null,
        );
        setEvidences(filteredGuestDamage as FileProps[]);
      }

      if (selectedGuestDamage.quotation_file) {
        setQuotationFile(selectedGuestDamage.quotation_file);
      }
    }
  }, [selectedGuestDamage]);

  return (
    <TrousseauFormContainer
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5 }}
      key="trousseauForm"
      transition={{
        duration: 0.4,
        ease: [0, 0.71, 0.2, 1.01],
      }}
    >
      <GridContainer>
        <FullDiv>
          <SimpleSelect
            id="itemName"
            options={trousseauDamageOptions}
            placeholder={formik.values.itemName ? formik.values.itemName : 'Selecione'}
            label="Qual item do enxoval foi danificado/extraviado? *"
            labelClassName="label"
            formik={formik}
          />
        </FullDiv>
        <NumberInput
          id="quantity"
          onValueChange={setQuantity}
          value={quantity}
          label="Quantidade *"
          labelClassName="label"
        />
        <TextField
          id="other"
          labelClassName="label"
          label={formik.values.itemName !== 'Outro' ? 'Outro' : 'Outro *'}
          formik={formik}
          disabled={formik.values.itemName !== 'Outro'}
          value={formik.values.other}
        />
        <SimpleSelect
          id="resolution"
          options={needToDoneOptions}
          placeholder={formik.values.resolution ? formik.values.resolution : 'Selecione'}
          label="O que precisa ser feito? *"
          labelClassName="label"
          formik={formik}
        />
        <div>
          <TextField
            id="itemPrice"
            labelClassName="label"
            label="Valor unitário"
            mask="money"
            placeholder="R$ 0,00"
            formik={formik}
            value={formik.values.itemPrice}
          />
          <p
            style={{
              marginTop: '0.5rem',
              fontSize: '1rem',
              color: '#B0BED8',
              display: 'flex',
              gap: '0.5rem',
            }}
          >
            <InfoOutlinedIcon />
            Caso seja adicionado mais de um item este valor será contabilizado conforme quantidade
            na seção de Resumo de danos
          </p>
        </div>
        <UploadInput
          label="Orçamento/nota fiscal"
          labelClassName="label"
          setUploadedFile={setQuotationFile}
          uploadedFile={quotationFile}
        />
        <TextField
          id="quotationLink"
          labelClassName="label"
          label="Link do orçamento/nota fiscal"
          placeholder="https://www.exemplo.com.br"
          formik={formik}
          value={formik.values.quotationLink}
        />
        <FullDiv>
          <UploadInput
            label="Evidências *"
            labelClassName="label"
            setUploadedFiles={setEvidences}
            uploadedFiles={evidences}
            isMultiple
            accept={['image/*', 'application/*', '.csv', 'video/mp4', 'video/quicktime', 'video/x-ms-wmv', 'video/webm', 'video/x-flv']}
            required
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
          />
        </FullDiv>
        <FullDiv>
          <TextField
            id="observation"
            labelClassName="label"
            label="Observações"
            placeholder="Digite aqui"
            formik={formik}
            type="textarea"
            value={formik.values.observation}
          />
        </FullDiv>
      </GridContainer>
      {(roles?.includes('Attendant') || roles?.includes('Host') || roles?.includes('Admin')) && (
        <ButtonContainer>
          <FormButton disable={errorMessage !== undefined}>
            {isEditingForm ? 'Editar Item' : 'Adicionar item'}
          </FormButton>
        </ButtonContainer>
      )}
    </TrousseauFormContainer>
  );
};

export default TrousseauForm;
